import React from 'react';
import PropTypes from 'prop-types';
import { serviceState } from '../../services/SandboxService';
import { deviceAppOf, deviceServiceOf } from '../../utils/deviceApps';
import './sandbox.css';

const LABELS = {
  building: 'Building',
  running: 'Running',
  crashed: 'Crashed',
  failed: 'Build failed',
  stopped: 'Stopped',
  // A command-line tool once built (utils/cliServices.js): ready, never "running".
  built: 'Built',
};

/**
 * A small pill with a service's state; restarts are added when there were any. A native device app
 * (utils/deviceApps.js: `device`, else what its runner entry or the project's answers say) never runs in the sandbox:
 * "iOS app" / "Android app" / "Device app" instead of its state.
 */
export default function ServiceState({ service = null, device = null }) {
  const app = device || deviceAppOf({ service }) || deviceServiceOf(service?.projectId, service?.serviceName);
  if (app && service?.containerStatus !== 'running') {
    const { Icon } = app;
    return (
      <span className="sbx-pill sbx-pill-device" data-testid="service-state-device" title={`${app.label}: built for phones (Build for devices), not run in the sandbox`}>
        {Icon ? <Icon aria-hidden size={12} /> : null}
        {app.chip}
      </span>
    );
  }
  const state = serviceState(service);
  const restarts = service?.restartCount || 0;
  const warn = state === 'running' && restarts > 0;
  return (
    <span className={`sbx-pill sbx-pill-${warn ? 'warn' : state}`}>
      {state === 'running' || state === 'building' ? <span aria-hidden="true" className="sbx-dot" /> : null}
      {LABELS[state] || LABELS.stopped}
      {warn ? ` · ${restarts} restart${restarts === 1 ? '' : 's'}` : ''}
    </span>
  );
}

ServiceState.propTypes = {
  // The service's device app (utils/deviceApps.js deviceAppOf), when the caller knows it.
  device: PropTypes.object,
  service: PropTypes.object,
};
